// appwrite.sdk.service.ts

import { Injectable } from '@angular/core';
import { Client, Account, Models, ID, OAuthProvider } from 'appwrite';
import {
  Observable,
  catchError,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({ providedIn: 'root' })
export class AppwriteService {
  client = new Client()
    .setEndpoint(environment.appwrite.url)
    .setProject(environment.appwrite.projectKey);
  account = new Account(this.client);

  tempAccount;

  constructor() {}

  // ----------------------- Preferences -----------------------
  setPreference(key: string, value: string) {
    return from(this.account.updatePrefs({ [key]: value }));
  }

  // ----------------------- Email Verification -----------------------
  emailVerificationRequest() {
    return from(
      this.account.createVerification(environment.appwrite.verificationRedirectURL)
    );
  }

  emailVerificationApply(userId: string, secret: string) {
    return this.account.updateVerification(userId, secret);
  }

  // ----------------------- Sessions -----------------------
  async getUserSessions() {
    const request$ = this.account.listSessions();
    return request$.then((response) => response);
  }

  async getUserCurrentSession() {
    const request$ = this.account.getSession('current');
    return request$.then((response) => response);
  }

  getUserCurrentUserAppwriteProfile() {
    return from(this.account.get());
  }

  async killSessionByID(id: string) {
    await this.account.deleteSession(id);
  }

  async clearAppwriteSession() {
    this.account
      .getSession('current')
      .then(async () => {
        await this.account.deleteSession('current');
        this.regenerateAppwriteClient();
      })
      .catch((error) => {
        console.log(error);
        this.regenerateAppwriteClient();
      });
  }

  /**
   * Forcefully clears any active session and resets the Appwrite client.
   * This handles cases where a session might exist but not be fully synchronized
   * or when switching accounts.
   */
  async forceClearSession(): Promise<void> {
    try {
      // First try to delete the 'current' session
      await this.account.deleteSession('current');
    } catch (error) {
      // If no session exists, this will throw an error, which we can safely ignore
      console.log('No active session to clear or error clearing session:', error);
    } finally {
      // Always regenerate the client to ensure a clean state
      this.regenerateAppwriteClient();
    }
  }

  // ----------------------- Client Management -----------------------
  regenerateAppwriteClient() {
    this.client = new Client()
      .setEndpoint(environment.appwrite.url)
      .setProject(environment.appwrite.projectKey);

    this.account = new Account(this.client);
    return this.account;
  }

  generateCleanAppwriteClient() {
    const client = new Client()
      .setEndpoint(environment.appwrite.url)
      .setProject(environment.appwrite.projectKey);
    const account = new Account(client);
    return account;
  }

  generateTempAccountToken(token: string) {
    const client = new Client()
      .setEndpoint(environment.appwrite.url)
      .setProject(environment.appwrite.projectKey)
      .setJWT(token);
    return new Account(client);
  }

  setCurrentToken(token: string) {
    this.client.setJWT(token);
    this.account = new Account(this.client);
  }

  // ----------------------- Token & JWT -----------------------
  createJWT() {
    return from(this.generateCleanAppwriteClient().createJWT());
  }

  refreshToken(): Observable<string> {
    return this.createJWT().pipe(
      switchMap((newToken: { jwt: string }) => {
        this.setCurrentToken(newToken.jwt);
        return of(newToken.jwt);
      })
    );
  }

  async isSessionValid(): Promise<boolean> {
    try {
      const user = await this.generateCleanAppwriteClient().get();
      return !!user;
    } catch (error) {
      console.error('Error checking Session validity:', error);
      return false;
    }
  }

  isTokenValidParse(token: string, meta?: any): boolean {
    try {
      const currentDate = Math.floor(new Date().getTime() / 1000);
      const decoded = jwtDecode(token);
      const tokenLifeTime = decoded.exp - currentDate;

      if (tokenLifeTime < 300) return false;
      return true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  isTokenValidObservable(token: string): Observable<boolean> {
    const account = this.generateTempAccountToken(token);

    return from(account.get()).pipe(
      map((user: any) => !!user),
      catchError((error: any) => {
        console.error('Error checking token validity:', error);
        return [false];
      })
    );
  }

  async isTokenValid(token: string): Promise<boolean> {
    try {
      const account = this.generateTempAccountToken(token);
      const user = await account.get();
      return !!user;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }

  // ----------------------- Authentication -----------------------
  loginWithEmailAndPassword(credentials: { email: string; password: string }) {
    return from(
      this.account.createEmailPasswordSession(credentials.email, credentials.password)
    );
  }

  loginWithMagicLink(credentials: { email: string }) {
    return from(
      this.account.createMagicURLToken(
        ID.unique(),
        credentials.email,
        environment.appwrite.oAuthCallbackUrl.magicLink
      )
    );
  }

  loginWithPhone(credentials: { phone: string }) {
    return from(this.account.createPhoneToken(ID.unique(), credentials.phone));
  }

  handleMagicLinkCallback(userId: string, secret: string) {
    return from(this.account.updateMagicURLSession(userId, secret));
  }

  handlePhoneCallback(userId: string, secret: string) {
    return from(this.account.updatePhoneSession(userId, secret));
  }

  // ----------------------- Password Recovery -----------------------
  createPasswordRecoveryRequest(email: string) {
    return from(
      this.account.createRecovery(
        email,
        environment.appwrite.resetPasswordCallbackUrl
      )
    );
  }

  confirmPasswordRecoveryRequest(data: {
    userId: string;
    secret: string;
    password: string;
  }) {
    return from(this.account.updateRecovery(data.userId, data.secret, data.password));
  }

  // ----------------------- OAuth -----------------------
  createOAuth2Session(provider: OAuthProvider = OAuthProvider.Google) {
    this.account.createOAuth2Session(
      provider,
      environment.appwrite.oAuthCallbackUrl.google,
      environment.appwrite.oAuthFailUrl
    );
  }

  // ----------------------- Account Creation -----------------------
  createNewAccountWithEmail(userInfo: {
    username?: string;
    firstname: string;
    lastname: string;
    email: string;
    password: string;
  }) {
    const username = this.generateUserName(userInfo);
    return from(
      this.account.create(
        username,
        userInfo.email,
        userInfo.password,
        `${userInfo.firstname} ${userInfo.lastname}`
      )
    );
  }

  generateUserName(userInfo: {
    firstname: string;
    lastname: string;
    email: string;
  }): string {
    const usernamePrefix = userInfo.firstname
      ? userInfo.firstname.charAt(0)
      : userInfo.email.charAt(0);
    const usernameBody = userInfo.lastname.toLowerCase();
    const randomSuffix = Math.floor(Math.random() * 1000);
    const username = `${usernamePrefix.toLowerCase()}${usernameBody}${randomSuffix}`;
    return username.replace(' ', '-');
  }

  // ----------------------- Webhook -----------------------
  async isWebhookDone() {
    const result = await this.account.get();
    return !!result.labels.includes('learmoRegistered');
  }
}
